/** Download and test a completed build. Builds are started from Studio. */
import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import type { AppSummary, BuildDto } from '@appbuilder/shared';
import { LIVE_BUILD_STATUSES } from '@appbuilder/shared';
import { appsApi, buildsApi, ApiClientError } from '../api/client';
import { Banner, Button, StatusBadge } from '../components/ui';

const POLL_INTERVAL_MS = 3000;
const isLive = (status: BuildDto['status']): boolean =>
  (LIVE_BUILD_STATUSES as readonly string[]).includes(status);

export function BuildPanelPage(): JSX.Element {
  const { appId = '' } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [app, setApp] = useState<AppSummary | null>(null);
  const [history, setHistory] = useState<BuildDto[]>([]);
  const [build, setBuild] = useState<BuildDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [busy, setBusy] = useState<'apk' | 'aab' | null>(null);
  const [cert, setCert] = useState<string | null>(null);
  const requestedId = searchParams.get('buildId');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([appsApi.get(appId), buildsApi.history(appId)])
      .then(async ([nextApp, builds]) => {
        let selected = builds.find((item) => item.id === requestedId);
        if (!selected && requestedId) {
          try {
            const requested = await buildsApi.get(requestedId);
            if (requested.appId === appId) selected = requested;
          } catch { /* Fall back to recent builds. */ }
        }
        if (cancelled) return;
        setApp(nextApp);
        setHistory(builds);
        setBuild(selected ?? builds.find((item) => isLive(item.status))
          ?? builds.find((item) => item.status === 'succeeded') ?? builds[0] ?? null);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof ApiClientError ? err.message : 'Could not load your builds.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [appId, requestedId]);

  useEffect(() => {
    if (!build || !isLive(build.status)) return;
    const buildId = build.id;
    const timer = setInterval(async () => {
      try {
        const next = await buildsApi.get(buildId);
        setBuild((current) => current?.id === buildId ? next : current);
        if (!isLive(next.status)) {
          buildsApi.history(appId).then(setHistory).catch(() => {});
        }
      } catch (err) {
        if (err instanceof ApiClientError) setError(err.message);
      }
    }, POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [appId, build?.id, build?.status]);

  const download = async (type: 'apk' | 'aab'): Promise<void> => {
    if (!build || build.status !== 'succeeded') return;
    setBusy(type);
    setDownloadError(null);
    try {
      const artifact = await buildsApi.artifact(build.id, type);
      if (artifact.certSha256) setCert(artifact.certSha256);
      window.location.href = artifact.downloadUrl;
    } catch (err) {
      setDownloadError(err instanceof ApiClientError ? err.message : 'Download failed. Please try again.');
    } finally {
      setBusy(null);
    }
  };

  const selectBuild = (item: BuildDto): void => {
    setBuild(item);
    setDownloadError(null);
    setCert(null);
    setSearchParams({ buildId: item.id });
  };

  const ready = build?.status === 'succeeded';
  const active = build ? isLive(build.status) : false;
  const backTo = app?.generation === 1 ? `/apps/${appId}` : '/apps';

  return (
    <div className="page build-download-page">
      <header className="build-download-header">
        <Link to={backTo} className="build-download-back">← {app?.generation === 1 ? 'Editor' : 'My apps'}</Link>
        <div className="build-download-eyebrow">APP DELIVERY</div>
        <h1>{app?.name ?? 'Your app'}</h1>
        <p>Test your app on Android, then get the Play Store file when you’re ready to publish.</p>
      </header>

      {error && <Banner>{error}</Banner>}

      <section className="build-download-hero" aria-labelledby="build-test-heading">
        <div className="build-download-hero-content">
          <span className="build-download-kicker"><span aria-hidden="true" className="build-download-live-dot" /> TEST ON YOUR PHONE</span>
          <h2 id="build-test-heading">See your app come to life.</h2>
          <p>Download the Android test build, install it on your phone, and explore the full experience.</p>
          {ready ? (
            <Button className="build-download-primary" loading={busy === 'apk'} disabled={busy !== null} onClick={() => void download('apk')}>
              <span className="build-download-button-icon" aria-hidden="true">↓</span>
              <span>Download test APK <small>Install and try your app</small></span>
              <span aria-hidden="true" className="build-download-arrow">↗</span>
            </Button>
          ) : (
            <div className="build-download-waiting" role="status">
              {loading ? 'Loading your build…' : active ? 'Your test build is on its way' : build?.status === 'failed' ? 'This build did not finish' : 'No test build is ready yet'}
            </div>
          )}
          {downloadError && <p className="build-download-error" role="alert">{downloadError}</p>}
          <p className="build-download-note">APK is for testing on Android devices.</p>
        </div>
        <div className="build-download-illustration" aria-hidden="true">
          <div className="build-download-orbit" />
          <div className="build-download-phone"><div className="build-download-phone-top" /><div className="build-download-phone-logo">✦</div><div className="build-download-phone-line" /><div className="build-download-phone-line short" /><div className="build-download-phone-tile" /></div>
        </div>
      </section>

      <section className="build-download-status" aria-label="Build status">
        <div className="build-download-status-icon" aria-hidden="true">{ready ? '✓' : active ? '◷' : 'i'}</div>
        <div>
          <strong>{ready ? 'Build complete' : active ? 'Building your app' : build?.status === 'failed' ? 'Build failed' : build?.status === 'canceled' ? 'Build canceled' : 'Your build will appear here'}</strong>
          <p>{ready && build ? `Ready since ${new Date(build.createdAt).toLocaleString()}` : active && build ? `${build.status === 'queued' && build.queuePosition ? `Position ${build.queuePosition} in queue. ` : ''}This page updates automatically.` : build?.error ?? 'Create and build your app in Studio, then come back to test it.'}</p>
        </div>
        {build && <StatusBadge status={build.status} kind="build" />}
      </section>

      <section className="build-download-publish" aria-labelledby="build-publish-heading">
        <div>
          <span className="build-download-section-label">WHEN YOU’RE READY</span>
          <h2 id="build-publish-heading">Publish on Google Play</h2>
          <p>Use the Android App Bundle for your Play Console release.</p>
        </div>
        <Button variant="secondary" className="build-download-secondary" loading={busy === 'aab'} disabled={!ready || busy !== null} onClick={() => void download('aab')}>
          Download AAB <span aria-hidden="true">↗</span>
        </Button>
      </section>

      {(history.length > 1 || cert || build?.logUrl) && (
        <details className="build-download-details">
          <summary>Build details and history <span aria-hidden="true">⌄</span></summary>
          <div className="build-download-details-body">
            {cert && <p className="build-download-certificate">Upload signing certificate SHA-256: <code>{cert}</code></p>}
            {build?.logUrl && <a href={build.logUrl} target="_blank" rel="noreferrer">View build log ↗</a>}
            {history.length > 1 && <div className="build-download-history">
              {history.map((item) => <button type="button" key={item.id} className={item.id === build?.id ? 'selected' : ''} onClick={() => selectBuild(item)}>
                <span><strong>{new Date(item.createdAt).toLocaleString()}</strong><small>{item.status}</small></span>
                <span aria-hidden="true">→</span>
              </button>)}
            </div>}
          </div>
        </details>
      )}
    </div>
  );
}
